import { DayHours } from "@anoki/fse-common";
import { Avatar, AvatarSize } from "../Avatar";
import { ColorKeys } from "@/theme/colors";
import { Divider } from "../Divider";
import { Button } from "../Button";
import "./CardDoctor.scss";
import { Col, Row } from "@/components";
import { HeadingLevel } from "@/types/headings";

type Info = {
  label: string;
  value: string;
};

type ScheduleDay = {
  label: string;
  slots: DayHours[];
};

type CardDoctorProps = {
  titleTag?: HeadingLevel;
  doctorName: string;
  specialties?: string[];
  contactInfo?: Info[];
  schedule?: {
    label: string;
    days: ScheduleDay[];
  };
  avatar?: {
    initials: string;
    bg?: ColorKeys;
    textColor?: ColorKeys;
  };
  avatarSize?: AvatarSize;
  button?: {
    label: string;
    onClick: () => void;
  };
};

export const CardDoctor = ({
  titleTag: TitleTag = "h2",
  doctorName,
  specialties,
  contactInfo,
  schedule,
  avatar,
  avatarSize = "3",
  button,
}: CardDoctorProps) => {
  const showContactInfo = () => {
    return contactInfo && contactInfo?.length > 0;
  };

  const showSchedule = () => {
    return schedule && schedule?.days?.length > 0;
  };

  const showFooter = () => {
    return button?.label && button?.onClick;
  };

  const showDivider = () => {
    return showContactInfo() || showSchedule();
  };

  return (
    <article className="bg-white shadow br-x4 ui-card-doctor">
      <Row as={"header"} gap={"x16"} className="align-items-center">
        {avatar && (
          <Avatar
            textColor={avatar?.textColor ?? "slate-20"}
            bg={avatar?.bg ?? "lightblue-a01"}
            initials={avatar?.initials ?? ""}
            className="ui-card-doctor-avatar"
            size={avatarSize}
          />
        )}

        <div>
          <TitleTag className="fs-5 md-fs-custom-2 fw-semibold text-slate-20">
            {doctorName}
          </TitleTag>
          {specialties && specialties?.length > 0 && (
            <p className="fs-6 text-slate-20 fw-regular">
              {specialties?.join(" | ")}
            </p>
          )}
        </div>
      </Row>
      <div className="ui-card-doctor-content">
        {showContactInfo() && (
          <dl className="ui-card-doctor-contact">
            {contactInfo?.map((info) => (
              <div
                key={info.label}
                role="group"
                className="ui-card-doctor-contact-detail"
              >
                <dt className="fs-custom fw-bold text-slate-28">
                  {info.label}
                </dt>
                <dd className="fs-6 fw-regular text-slate-28">{info.value}</dd>
              </div>
            ))}
          </dl>
        )}

        {showDivider() && <Divider bg="border-divider" />}

        {showSchedule() && (
          <div>
            <div className={"ui-border-lg"}>
              <h1 className="text-align-left fw-bold text-slate-28 mb-x8 fs-custom">
                {schedule?.label}
              </h1>

              <div role="table" className="ui-card-doctor-table">
                {schedule?.days.map((day, dayIndex) => (
                  <div
                    key={day.label}
                    role="row"
                    className="ui-card-doctor-row"
                  >
                    <div
                      className="fs-6 fw-regular text-slate-28"
                      role="rowheader"
                    >
                      {day.label}
                    </div>
                    <Col
                      role="cell"
                      key={`cell-${dayIndex}`}
                      className="fs-6 fw-regular text-slate-28"
                    >
                      {day?.slots?.length === 0 && (
                        <div>
                          <span aria-hidden="true">-</span>
                        </div>
                      )}
                      {day.slots.map((slot, slotIndex) => (
                        <div key={`slot-${slotIndex}`}>
                          <time dateTime={slot.from}>{slot.from}</time>
                          <span aria-hidden="true"> - </span>
                          <time dateTime={slot.to}>{slot.to}</time>
                        </div>
                      ))}
                    </Col>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      {showFooter() && (
        <footer>
          <Button className="ui-card-doctor-btn" onClick={button?.onClick}>
            {button?.label}
          </Button>
        </footer>
      )}
    </article>
  );
};
